import { useEffect } from 'react';
import { useSEO } from '@/hooks/useSEO';
import Footer from '@/components/feature/Footer';
import { trackEvent } from './lib/tracking';
import { WHATSAPP_BASE, WHATSAPP_GENERIC_MESSAGE } from './lib/constants';
import LeanHeaderCarp from './components/LeanHeader';
import MobileCtaBar from './components/MobileCtaBar';
import HeroSection from './components/HeroSection';
import PorQueSection from './components/PorQueSection';
import ModularSection from './components/ModularSection';
import AplicacoesSection from './components/AplicacoesSection';
import ProjetoSection from './components/ProjetoSection';
import IntegracaoSection from './components/IntegracaoSection';
import AcabamentoSection from './components/AcabamentoSection';
import QualificacaoForm from './components/QualificacaoForm';
import FaqSection, { faqItems } from './components/FaqSection';

export default function CarpeteCorporativoPage() {
  useSEO({
    title: 'Carpete Corporativo | Carpete em Placas para Escritórios',
    description:
      'Carpete corporativo para escritórios e empresas. Especificação, fornecimento e instalação de carpete modular e carpete em placas.',
    keywords:
      'carpete corporativo, carpete para escritório, carpete em placas, carpete modular, instalação de carpete corporativo, piso corporativo',
    path: '/carpete-corporativo',
  });

  useEffect(() => {
    let fired50 = false;
    let fired90 = false;
    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return;
      const pct = (window.scrollY / total) * 100;
      if (!fired50 && pct >= 50) { fired50 = true; trackEvent('scroll_50'); }
      if (!fired90 && pct >= 90) { fired90 = true; trackEvent('scroll_90'); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const baseUrl = import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com';
    const pageUrl = `${baseUrl}/carpete-corporativo`;

    const schemas: Array<{ id: string; data: Record<string, unknown> }> = [
      {
        id: 'schema-carp-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Início', item: baseUrl },
            { '@type': 'ListItem', position: 2, name: 'Soluções', item: `${baseUrl}/produtos` },
            { '@type': 'ListItem', position: 3, name: 'Carpete Corporativo', item: pageUrl },
          ],
        },
      },
      {
        id: 'schema-carp-service',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Carpete Corporativo',
          serviceType: 'Especificação, fornecimento e instalação de carpete corporativo',
          description:
            'Carpete corporativo para escritórios e empresas. Especificação, fornecimento e instalação de carpete modular e carpete em placas.',
          url: pageUrl,
          areaServed: ['Pernambuco', 'Nordeste'],
          provider: {
            '@type': 'LocalBusiness',
            name: 'Home Office Premium',
            telephone: '+55-81-98203-0285',
            url: baseUrl,
          },
        },
      },
      {
        id: 'schema-carp-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        },
      },
    ];

    const scripts: HTMLScriptElement[] = [];
    schemas.forEach(({ id, data }) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
      scripts.push(script);
    });

    return () => { scripts.forEach((s) => s?.remove()); };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <LeanHeaderCarp />

      <HeroSection />
      <PorQueSection />
      <ModularSection />
      <AplicacoesSection />
      <ProjetoSection />
      <IntegracaoSection />
      <AcabamentoSection />

      {/* Seção investimento */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="max-w-3xl mx-auto bg-background-50 border border-background-200/70 rounded-3xl p-8 md:p-14 text-center">
            <div className="inline-flex items-center gap-3 bg-white border border-background-200/70 rounded-full px-5 py-2.5 mb-7">
              <i className="ri-arrow-right-up-line text-primary-500 text-lg"></i>
              <span className="text-foreground-800 text-[14px] font-medium">
                Atendimento prioritário para projetos{' '}
                <span className="text-primary-600 font-semibold">a partir de R$ 50 mil</span>
              </span>
            </div>
            <h2 className="text-foreground-950 text-[24px] md:text-[32px] font-semibold tracking-tight leading-tight mb-5">
              Soluções para projetos corporativos de maior porte.
            </h2>
            <p className="text-foreground-600 text-[14px] md:text-[16px] leading-relaxed max-w-2xl mx-auto">
              O investimento depende da área, do produto especificado, da preparação do contrapiso
              e dos serviços envolvidos. Nosso foco são empresas que buscam uma solução profissional
              e estruturada — não apenas o fornecimento de um rolo de carpete.
            </p>
          </div>
        </div>
      </section>

      {/* Seção de qualificação do lead */}
      <section id="analise" className="bg-background-50 py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-2">
              <p className="text-[12px] md:text-[13px] font-semibold tracking-[0.28em] uppercase text-primary-500 mb-4">
                Análise do ambiente
              </p>
              <h2 className="text-foreground-950 text-[28px] md:text-[40px] font-semibold tracking-tight leading-tight mb-6">
                Conte um pouco sobre o seu ambiente.
              </h2>
              <p className="text-foreground-500 text-[15px] md:text-[16px] leading-relaxed">
                Responda algumas perguntas rápidas e continue o atendimento direto no WhatsApp —
                suas respostas já seguem preenchidas na mensagem para agilizar a análise.
              </p>
            </div>

            <div className="lg:col-span-3">
              <QualificacaoForm />
            </div>
          </div>
        </div>
      </section>

      <FaqSection />
      <Footer />

      {/* Barra CTA mobile */}
      <MobileCtaBar />

      {/* WhatsApp float (desktop) */}
      <a
        href={`${WHATSAPP_BASE}?text=${WHATSAPP_GENERIC_MESSAGE}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent('whatsapp_carpete', { location: 'float' })}
        className="fixed bottom-5 right-5 z-[9999] hidden md:flex w-12 h-12 md:w-14 md:h-14 items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all duration-300 cursor-pointer shadow-[0_2px_12px_rgba(0,0,0,0.12)]"
        aria-label="Falar com a Home Office Premium no WhatsApp"
      >
        <i className="ri-whatsapp-line text-xl md:text-2xl"></i>
      </a>
    </div>
  );
}